import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'

import {
  formatShortDate,
  getTimezoneDate,
  sort,
  timezones,
} from '../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'

import { ApplePassButton } from '../QRTicketCard/ApplePassButton'
import { GooglePassButton } from '../QRTicketCard/GooglePassButton'
import AccessPassTransferModal from './AccessPassTransferModal'

import placeholder from '../../assets/placeholder.png'

import './accessPassCard.scss'

function getPassTypeLabel(type) {
  return type === 'organizer' ? 'All-access pass' : 'Season pass'
}

function getValidThroughDate(events, timezone) {
  const sortedEvents = sort(events || [])
  const lastEvent = sortedEvents[sortedEvents.length - 1]
  if (!lastEvent?.start) return null

  return getTimezoneDate(lastEvent.start, timezone)?.format('MMM YYYY')
}

function formatNextEventDate(event, timezone) {
  const eventStart = getTimezoneDate(event?.start, timezone)
  if (!eventStart) return null

  if (!event?.display_start_time) {
    return formatShortDate(eventStart, 'dateOnly')
  }

  return formatShortDate(eventStart)
}

function getSectionLabel(accessPass) {
  if (accessPass?.generalAdmission) {
    return accessPass?.sectionNumber ? `GA ${accessPass.sectionNumber}` : 'GA'
  }
  if (accessPass?.sectionNumber) {
    return `Sec ${accessPass.sectionNumber}`
  }
  return null
}

function AccessPassEventRow({ event, timezone, accessPass, highlighted }) {
  const eventStart = getTimezoneDate(event?.start, timezone)
  const sectionLabel = highlighted ? getSectionLabel(accessPass) : null
  const isAttended = event?.status === 'complete'

  const content = (
    <Stack direction="horizontal" gap={3} className="align-items-center">
      <div className="access-pass-card__event-thumb">
        <Card.Img
          src={event?.image?.url || placeholder}
          alt={`Cover art for ${event?.name}`}
        />
      </div>
      <Stack gap={1} className="flex-grow-1 min-w-0">
        <Card.Title as="h6" className="card-title-sm mb-0 text-truncate">
          {event?.name}
        </Card.Title>
        <Card.Subtitle className="mb-0 small access-pass-card__event-meta">
          {highlighted ? (
            <>
              {event?.venue?.name}
              {eventStart && (
                <>
                  {' · '}
                  {formatNextEventDate(event, timezone)}
                </>
              )}
            </>
          ) : (
            eventStart && formatShortDate(eventStart, 'dateOnly')
          )}
        </Card.Subtitle>
      </Stack>
      {sectionLabel ? (
        <Badge className="access-pass-card__section-badge flex-shrink-0">
          {sectionLabel}
        </Badge>
      ) : (
        <Badge className="access-pass-card__status-badge flex-shrink-0">
          {isAttended ? 'Attended' : 'Upcoming'}
        </Badge>
      )}
    </Stack>
  )

  if (highlighted) {
    return (
      <Card className="access-pass-card__next-up m-0">
        <Card.Body>{content}</Card.Body>
      </Card>
    )
  }

  return <div className="access-pass-card__event-row">{content}</div>
}

export default function AccessPassCard({
  accessPass,
  eventPackage,
  onTransferred,
}) {
  const [showQR, setShowQR] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)

  const events = sort(accessPass?.events || [])
  const upcomingEvents = events.filter((event) => event?.status !== 'complete')
  const nextEvent = upcomingEvents[0] || events[0]
  const attendedCount = events.filter(
    (event) => event?.status === 'complete'
  ).length
  const remainingCount = events.length - attendedCount
  const timezone = timezones(
    nextEvent?.venue?.timezone || events[0]?.venue?.timezone
  )
  const validThrough = getValidThroughDate(events, timezone)

  const walletEvent =
    sort(eventPackage?.events)?.[0] || sort(accessPass?.events)?.[0] || null

  const walletObj = {
    ...accessPass,
    uuid: accessPass?.uuid,
    checkInCode: accessPass?.checkInCode,
    sectionNumber: accessPass?.sectionNumber,
    rowNumber: accessPass?.rowNumber,
    seatNumber: accessPass?.seatNumber,
    generalAdmission: accessPass?.generalAdmission,
    name: accessPass?.name,
    accessPass: true,
  }

  const artworkUrl = accessPass?.artwork?.url
  const backgroundColor = accessPass?.backgroundColor
  const fontColor = accessPass?.fontColor
  const hasCustomColors = Boolean(backgroundColor || fontColor)
  const headerStyle = hasCustomColors
    ? {
        ...(backgroundColor ? { backgroundColor } : {}),
        ...(fontColor ? { color: fontColor } : {}),
      }
    : undefined
  const mutedFontStyle = fontColor
    ? { color: fontColor, opacity: 0.82 }
    : undefined
  const qrButtonStyle = fontColor
    ? {
        backgroundColor: `${fontColor}1A`,
        border: `1px solid ${fontColor}33`,
      }
    : undefined
  const dividerStyle = fontColor
    ? { borderColor: `${fontColor}29`, opacity: 1 }
    : undefined
  const headerQrProps =
    fontColor && backgroundColor
      ? { fgColor: fontColor, bgColor: backgroundColor }
      : fontColor
        ? { fgColor: fontColor, bgColor: backgroundColor || '#23262f' }
        : backgroundColor
          ? { fgColor: fontColor || '#FFFFFF', bgColor: backgroundColor }
          : {}

  return (
    <>
      <Card className="access-pass-card card-xl">
        <div
          className={`access-pass-card__header${
            hasCustomColors ? ' access-pass-card__header--custom' : ''
          }`}
          style={headerStyle}
        >
          <Stack direction="horizontal" className="align-items-start gap-3">
            {artworkUrl && (
              <div className="access-pass-card__header-artwork">
                <Card.Img
                  src={artworkUrl}
                  alt={`Artwork for ${accessPass?.name || 'Access Pass'}`}
                />
              </div>
            )}
            <Stack gap={0} className="flex-grow-1 min-w-0">
              <Stack
                direction="horizontal"
                className="align-items-start justify-content-between gap-3"
              >
                <div className="min-w-0">
                  <p
                    className="caption--uppercase access-pass-card__label mb-1"
                    style={mutedFontStyle}
                  >
                    {getPassTypeLabel(accessPass?.type)}
                  </p>
                  <Card.Title as="h5" className="normal normal-bold mb-1">
                    {accessPass?.name || 'Access Pass'}
                  </Card.Title>
                  {accessPass?.checkInCode && (
                    <p
                      className="small access-pass-card__meta mb-0"
                      style={mutedFontStyle}
                    >
                      Pass #{accessPass.checkInCode}
                    </p>
                  )}
                </div>
                {accessPass?.checkInCode && (
                  <button
                    type="button"
                    className="access-pass-card__qr-btn"
                    aria-label="Show pass QR code"
                    style={qrButtonStyle}
                    onClick={() => setShowQR(true)}
                  >
                    <QRCodeSVG
                      value={accessPass.checkInCode}
                      size={48}
                      {...headerQrProps}
                    />
                  </button>
                )}
              </Stack>
              <hr className="my-3" style={dividerStyle} />
              <Stack
                direction="horizontal"
                className="justify-content-between gap-3"
              >
                <div>
                  <p
                    className="caption access-pass-card__label mb-0"
                    style={mutedFontStyle}
                  >
                    Events included
                  </p>
                  <p className="small normal-bold access-pass-card__stat-value mb-0">
                    {remainingCount} of {events.length} remaining
                  </p>
                </div>
                {validThrough && (
                  <div className="text-end">
                    <p
                      className="caption access-pass-card__label mb-0"
                      style={mutedFontStyle}
                    >
                      Valid through
                    </p>
                    <p className="small normal-bold access-pass-card__stat-value mb-0">
                      {validThrough}
                    </p>
                  </div>
                )}
              </Stack>
            </Stack>
          </Stack>
        </div>

        <Card.Body className="access-pass-card__body">
          {nextEvent && (
            <div className="mb-4">
              <p className="access-pass-card__section-label">Next up</p>
              <AccessPassEventRow
                event={nextEvent}
                timezone={timezone}
                accessPass={accessPass}
                highlighted
              />
            </div>
          )}

          {events.length > 0 && (
            <div>
              <p className="access-pass-card__section-label">All events</p>
              <div>
                {events.map((event) => (
                  <AccessPassEventRow
                    key={event.uuid}
                    event={event}
                    timezone={timezones(event?.venue?.timezone) || timezone}
                    accessPass={accessPass}
                  />
                ))}
              </div>
            </div>
          )}

          {walletEvent && (
            <div className="access-pass-card__wallet mt-4">
              <Stack
                direction="horizontal"
                gap={3}
                className="btn-group-flex flex-wrap mt-0 justify-content-center"
              >
                <ApplePassButton
                  alwaysShow
                  event={walletEvent}
                  obj={walletObj}
                />
                <GooglePassButton
                  alwaysShow
                  event={walletEvent}
                  obj={walletObj}
                />
              </Stack>
            </div>
          )}
          <Button
            variant="outline-light"
            className="mt-3 w-100"
            onClick={() => setShowTransfer(true)}
          >
            Transfer access pass
          </Button>
        </Card.Body>
      </Card>

      <Modal show={showQR} onHide={() => setShowQR(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{accessPass?.name || 'Access Pass'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="access-pass-card__qr-modal-body text-center">
          {accessPass?.checkInCode && (
            <QRCodeSVG
              width="217"
              height="217"
              className="access-pass-card__qr-modal-code mb-3 p-0"
              value={accessPass.checkInCode}
            />
          )}
          <p className="small text-secondary mb-0">
            Show this code at entry for any included event.
          </p>
          {walletEvent && (
            <Stack
              direction="horizontal"
              gap={3}
              className="access-pass-card__modal-wallet btn-group-flex flex-wrap mt-4 justify-content-center"
            >
              <ApplePassButton alwaysShow event={walletEvent} obj={walletObj} />
              <GooglePassButton
                alwaysShow
                event={walletEvent}
                obj={walletObj}
              />
            </Stack>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowQR(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <AccessPassTransferModal
        accessPass={accessPass}
        show={showTransfer}
        onHide={() => setShowTransfer(false)}
        onTransferred={onTransferred}
      />
    </>
  )
}
